import React, { useState, useEffect } from 'react';
import {
  BookOpen,
  X,
  Info,
  ReceiptText,
  CircleDollarSign,
  Search,
  Banknote,
  FileOutput,
} from 'lucide-react';
import '../CSS/GuideModal.css';

const SchoolFeeGuideModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    if (isOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) setIsOpen(false);
  };

  return (
    <>
      {/* ── Floating Action Button ── */}
      <div className="gml-fab-wrapper">
        <span className="gml-fab-tooltip">Need help?</span>
        <button
          className="gml-fab"
          onClick={() => setIsOpen(true)}
          aria-label="Open School Fee Guide"
          title="School Fee Management Guide"
        >
          <BookOpen className="gml-fab-icon" />
        </button>
      </div>

      {/* ── Modal ── */}
      {isOpen && (
        <div
          className="gml-backdrop"
          onClick={handleBackdropClick}
          role="dialog"
          aria-modal="true"
          aria-labelledby="gml-modal-title"
        >
          <div className="gml-modal">

            {/* Header */}
            <div className="gml-modal-header">
              <div className="gml-modal-title-wrap">
                <BookOpen className="gml-modal-header-icon" />
                <h2 className="gml-modal-title" id="gml-modal-title">
                  School Fee Module Guide
                </h2>
              </div>
              <button
                className="gml-modal-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close guide"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="gml-modal-body">

              {/* Welcome Banner */}
              <div className="gml-welcome-banner">
                <Info className="gml-welcome-icon" />
                <div>
                  <p className="gml-welcome-heading">
                    Welcome to the School Fee Module!
                  </p>
                  <p className="gml-welcome-text">
                    This comprehensive guide will help you navigate and utilize
                    all features of the School Fee component effectively. Use it
                    anytime you need a quick reference.
                  </p>
                </div>
              </div>

              {/* ── Key Features Overview ── */}
              <h3 className="gml-section-heading">Key Features Overview</h3>
              <div className="gml-cards-grid">

                {/* Submitted Fee Records */}
                <div className="gml-feature-card gml-feature-card--blue">
                  <div className="gml-card-head gml-card-head--blue">
                    <ReceiptText className="gml-card-head-icon" />
                    <h4 className="gml-card-head-title">Submitted Fee Records</h4>
                  </div>
                  <div className="gml-card-body">
                    <ul className="gml-card-list">
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">1</span>
                        View all submitted fee payments
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">2</span>
                        Advanced filtering by class and student name
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">3</span>
                        Edit or delete existing fee records
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">4</span>
                        Generate invoices, receipts, and overdue notices
                      </li>
                    </ul>
                  </div>
                </div>

                {/* New Fee Entries */}
                <div className="gml-feature-card gml-feature-card--green">
                  <div className="gml-card-head gml-card-head--green">
                    <CircleDollarSign className="gml-card-head-icon" />
                    <h4 className="gml-card-head-title">New Fee Entries</h4>
                  </div>
                  <div className="gml-card-body">
                    <ul className="gml-card-list">
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">1</span>
                        Record new school fee payments
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">2</span>
                        Record miscellaneous fee payments
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">3</span>
                        Submit individual or bulk payments
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">4</span>
                        View enrollment status indicators
                      </li>
                    </ul>
                  </div>
                </div>

              </div>

              {/* ── Detailed Feature Guide ── */}
              <h3 className="gml-section-heading">Detailed Feature Guide</h3>
              <div className="gml-detail-section">

                {/* Working with Submitted Fee Records */}
                <div className="gml-detail-card gml-detail-card--cyan">
                  <div className="gml-detail-head gml-detail-head--cyan">
                    <Search className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Working with Submitted Fee Records</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--cyan">Search and Filter</p>
                    <ul className="gml-detail-list">
                      <li>Use the search bar to find specific students by name</li>
                      <li>Filter by class using the dropdown to view only specific class records</li>
                      <li>Sort results by student name or class in ascending / descending order</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--cyan">Student Actions</p>
                    <ul className="gml-detail-list">
                      <li><strong>Hover over student names</strong> to reveal the action menu</li>
                      <li>
                        <span className="gml-inline-badge gml-inline-badge--success">Create Invoice</span>
                        — Generate professional invoices
                      </li>
                      <li>
                        <span className="gml-inline-badge gml-inline-badge--success">Create Receipt</span>
                        — Generate payment receipts
                      </li>
                      <li>
                        <span className="gml-inline-badge gml-inline-badge--muted">Overdue Reminder</span>
                        — Create overdue notices
                      </li>
                      <li><strong>Overdue Statement</strong> — Generate formal overdue statements</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--cyan">Editing Records</p>
                    <ul className="gml-detail-list">
                      <li>Click the edit (pencil) button to modify payment amounts</li>
                      <li>Click the delete (trash) button to remove fee records</li>
                      <li>Outstanding amounts update automatically based on payments</li>
                      <li>Export fee records to Excel, Word, or PDF formats</li>
                    </ul>
                  </div>
                </div>

                {/* Recording New Fee Payments */}
                <div className="gml-detail-card gml-detail-card--yellow">
                  <div className="gml-detail-head gml-detail-head--yellow">
                    <Banknote className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Recording New Fee Payments</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--yellow">Student Selection</p>
                    <ul className="gml-detail-list">
                      <li>Search students by name or filter by class</li>
                      <li>Sort the student list for easier navigation</li>
                      <li>Color indicators show enrollment status (current, expelled, passed out, withdrawn)</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--yellow">School Fee Payments</p>
                    <ul className="gml-detail-list">
                      <li>School fees are automatically calculated based on class level</li>
                      <li>Enter payment amounts in the "Record School Fee Payment" field</li>
                      <li>Outstanding amounts update automatically</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--yellow">Miscellaneous Fees</p>
                    <ul className="gml-detail-list">
                      <li>Standard fees include SRC, ID, Uniform, and Computing fees</li>
                      <li>Additional fees for classes 10 &amp; 12 (Gown, Certificate processing)</li>
                      <li>
                        <span className="gml-inline-badge gml-inline-badge--muted">Restricted</span>
                        — Expelled, Passed Out, or Withdrawn students cannot have payments recorded
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Document Generation */}
                <div className="gml-detail-card gml-detail-card--green">
                  <div className="gml-detail-head gml-detail-head--green">
                    <FileOutput className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Document Generation</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--green">Invoice Creation</p>
                    <ul className="gml-detail-list">
                      <li>Professional invoice template with school branding</li>
                      <li>Automatic invoice numbering</li>
                      <li>Includes school fee summary with amounts paid and outstanding</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--green">Receipt Generation</p>
                    <ul className="gml-detail-list">
                      <li>Official receipt template with school branding</li>
                      <li>Includes payment method and amount in words</li>
                      <li>Automatic receipt numbering with outstanding balance shown</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--green">Overdue Documents &amp; Options</p>
                    <ul className="gml-detail-list">
                      <li><strong>Overdue Reminder:</strong> Simple notice for outstanding fees</li>
                      <li><strong>Overdue Statement:</strong> Formal document with full fee details</li>
                      <li>Save as PDF for digital records or print directly from the browser</li>
                      <li>Add custom content and signatory information to any document</li>
                    </ul>
                  </div>
                </div>

              </div>

              {/* ── Best Practices ── */}
              <div className="gml-best-practices">
                <p className="gml-best-practices-heading">Tips for Effective Use</p>
                <ul className="gml-best-practices-list">
                  {[
                    'Pay attention to color-coded enrollment status indicators before recording payments.',
                    'Generate receipts immediately after receiving payments for accurate records.',
                    'Use overdue documents promptly to follow up on unpaid fees.',
                    'Export fee data regularly for backup and reporting purposes.',
                    'Use the statistics view to monitor overall fee collection progress.',
                    'Access this guide anytime via the floating button on the bottom-right of the screen.',
                  ].map((tip, i) => (
                    <li key={i}>
                      <span className="gml-bp-dot" aria-hidden="true" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>

            </div>

            {/* Footer */}
            <div className="gml-modal-footer">
              <button className="gml-close-btn" onClick={() => setIsOpen(false)}>
                Got it
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default SchoolFeeGuideModal;